import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import { showModal } from '../../../../actions/modal';
import { fetchBillingDocs } from '../../../../actions/finances';
import { BILL_DOCS_COUNT_ON_PAGE, FINANCES_TABS, FINANCES_THEAD_ITEMS } from '../../../../config';

import FilterBlock from './FilterBlock';
import ExpenseTable from '../ExpenseTable';
import BillTable from '../BillTable';
import PaymentTable from '../PaymentTable';
import ReportTable from '../ReportTable';
import SpinnerBlock from '../../../common/SpinnerBlock';
import qs from 'qs';
import { useAutoTranslation } from '../../../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  width: 100%;
  background: #ffffff;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 150px;
  margin-top: 40px;
  position: relative;
  .fixed-table-container {
    overflow-x: auto;
    padding-bottom: 140px;
  }
  .finance-table__heading {
    margin-bottom: 24px;
    h4 {
      font-weight: 500;
      font-size: 14px;
      line-height: 20px;
      margin-bottom: 8px;
    }
    color: #6e768e;
    strong {
      color: #343a40;
      font-weight: 700;
    }
  }
  .finance-table__heading-elems {
    display: flex;
  }
  .finance-table__heading-elem {
    font-size: 14px;
  }
  .finance-table__heading-elem:first-child {
    margin-right: 24px;
  }
  @media (max-width: 540px) {
    padding: 15px;
    .finance-table__heading-elems {
      flex-wrap: wrap;
    }
    .finance-table__heading-elem {
      margin-bottom: 5px;
    }
    .finance-table__heading-elem strong {
      display: block;
    }
  }
`;

const FinancesTabsWrapper = styled('div')`
  .nav-tabs {
    position: absolute;
    top: -37px;
    left: 0;
    border-bottom: none;
  }
  .nav-tabs .nav-item.show .nav-link,
  .nav-tabs .nav-link.active {
    color: #7e57c2;
    background-color: #fff;
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.1);
  }

  .nav-tabs .nav-link-after {
    content: '';

    left: 0px;
    width: 100%;
    height: 5px;
    background-color: #fff;
    border: none;
  }

  .nav-tabs .nav-link-after {
    border-top: 1px solid #edf0f2;

    margin-top: -1px;
  }

  .nav-tabs .nav-link-after.active {
    border-top: 0px;
    margin-top: 0px;
  }

  .nav-tabs .nav-link.active .badge {
    background-color: #7e57c2;
    color: #fff;
  }

  .nav-tabs .nav-link .badge {
    background-color: #dfe4e6;
    color: #828489;
    margin-left: 10px;
    font-weight: 600;
  }

  .nav-tabs .nav-link .badge {
  }

  .nav-tabs .nav-item {
    margin-right: 4px;
  }

  .nav-tabs .nav-link {
    border: 1px solid transparent;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    background-color: #fbfcfd;
  }

  .nav-tabs .nav-link.active {
    border: 1px solid transparent;
  }

  .nav-tabs .nav-link:hover {
    background-color: #fff;
  }

  .nav-tabs > li > a {
    color: #7a7c82;
    font-weight: 600;
  }

  .nav-link {
    display: block;
    padding: 0.5rem 1rem;
    cursor: pointer;
  }

  .nav-link .nav-link-icon {
    padding-top: 1px;
    margin-right: 3px;
  }

  .nav-link i {
    font-size: 1rem;
    margin-right: 3px;
    margin-top: 4px;
  }

  @media (max-width: 520px) {
    .nav-link {
      padding-right: 5px;
      padding-left: 5px;
      .badge {
        display: none;
      }
    }
  }
`;

const tables = {
  EXPENSE: ExpenseTable,
  PAYMENT: PaymentTable,
  BILL: BillTable,
  REPORT: ReportTable,
};

const Table = ({
  type,
  items,
  totalItems,
  page,
  handlePageSelect,
  perPageCount,
  setPerPageCount,
  setActiveTab,
  urlSearchStr,
}) => {
  let Component = null;
  if (tables[type]) {
    Component = tables[type];
  } else {
    Component = tables['EXPENSE'];
  }
  return (
    <Component
      THEAD_ITEMS={FINANCES_THEAD_ITEMS[type] || FINANCES_THEAD_ITEMS['EXPENSE']}
      items={items}
      totalItems={totalItems}
      page={page}
      handlePageSelect={handlePageSelect}
      perPageCount={perPageCount}
      setPerPageCount={setPerPageCount}
      urlSearchStr={urlSearchStr}
    />
  );
};

const MainBlock = (props) => {
  const dispatch = useDispatch();
  const { history } = props;
  const billingDocs = useSelector((state) => state.finances.data);
  const totalItems = useSelector((state) => state.finances.totalItems);

  const { t } = useAutoTranslation();

  const [loading, setLoading] = useState(false);
  const [perPageCount, setPerPageCount] = useState(BILL_DOCS_COUNT_ON_PAGE);

  const [page, setPage] = useState(0);
  const [activeTab, setActiveTab] = useState('EXPENSE');
  const [dateTo, setDateTo] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [urlSearchStr, setUrlSearchStr] = useState(null);

  const handleExportModal = () => {
    dispatch(showModal('ExportFinances'));
  };
  const [init, setInit] = useState(0);

  const getFilterTypes = (tab) => {
    return tab === 'EXPENSE' ? 'EXPENSE,PAYMENT,REPORT' : tab;
  };

  const getSearchStr = (activeTab, page, dateTo, dateFrom) => {
    let result = '?';
    if (activeTab) {
      result += `active_tab=${activeTab}&`;
    }
    if (page) {
      result += `page=${page}&`;
    }
    if (dateTo) {
      result += `date_to=${dateTo}&`;
    }
    if (dateFrom) {
      result += `date_from=${dateFrom}&`;
    }
    return result.slice(0, -1);
  };

  const parseSearchStr = (str) => {
    let result = qs.parse(str.slice(1));
    return {
      activeTab: result.active_tab,
      dateFrom: result.date_from,
      dateTo: result.date_to,
      page: result.page,
    };
  };

  useEffect(() => {
    if (history.location.search) {
      const parseSearch = parseSearchStr(history.location.search);
      setActiveTab(parseSearch.activeTab);
      if (parseSearch.dateTo) {
        setDateTo(parseSearch.dateTo);
      }
      if (parseSearch.dateFrom) {
        setDateFrom(parseSearch.dateFrom);
      }
      if (parseSearch.page) {
        setPage(parseSearch.page);
      }
    } else {
      (async function () {
        setLoading(true);
        await dispatch(
          fetchBillingDocs({
            size: perPageCount,
            page: page,
            filter_types: getFilterTypes(activeTab),
            date_from: dateFrom,
            date_to: dateTo,
          }),
        );
        setLoading(false);
      })();
    }
  }, []);

  useEffect(() => {
    if (activeTab || page || dateTo || dateFrom) {
      const newUrlSearchStr = getSearchStr(activeTab, page, dateTo, dateFrom);
      setUrlSearchStr(newUrlSearchStr);
    }
  }, [activeTab, dateFrom, dateTo, page]);

  useEffect(() => {
    if (init) {
      (async function () {
        setLoading(true);
        await dispatch(
          fetchBillingDocs({
            size: perPageCount,
            page: page,
            filter_types: getFilterTypes(activeTab),
            date_from: dateFrom,
            date_to: dateTo,
          }),
        );
        setLoading(false);
      })();
    }
    setInit(1);
  }, [page, perPageCount, activeTab, dateTo, dateFrom]);

  const handleOpenTab = (tab) => {
    setPage(0);
    setActiveTab(tab);
  };

  return (
    <>
      <Wrapper>
        <FinancesTabsWrapper className="finances-main__tabs">
          <ul className="nav nav-tabs">
            {FINANCES_TABS.map((tab) => (
              <li className="nav-item" key={tab.value}>
                <div
                  data-toggle="tab"
                  aria-expanded="true"
                  className={`nav-link ${activeTab === tab.value ? 'active' : ''}`}
                  onClick={() => handleOpenTab(tab.value)}
                >
                  <div className="tab-title">{t(tab.title)}</div>
                </div>
                <div className={`nav-link-after ${activeTab === tab.value ? 'active' : ''}`} />
              </li>
            ))}
          </ul>
        </FinancesTabsWrapper>
        <div className="finance-table__top">
          <FilterBlock
            handleExportModal={handleExportModal}
            dateTo={dateTo}
            setDateTo={setDateTo}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
          />
          {!loading ? (
            <Table
              type={activeTab}
              items={billingDocs}
              totalItems={totalItems}
              page={page}
              handlePageSelect={setPage}
              perPageCount={perPageCount}
              setPerPageCount={setPerPageCount}
              setActiveTab={setActiveTab}
              urlSearchStr={urlSearchStr}
            />
          ) : null}
          {loading ? (
            <div style={{ width: '100%', position: 'relative', height: '150px ' }}>
              <SpinnerBlock show={loading} />
            </div>
          ) : null}
        </div>
      </Wrapper>
    </>
  );
};

export default MainBlock;
